import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home, BookOpen, Search } from "lucide-react";
import siteLogo, { SITE_LOGO_ALT } from "@/assets/brandLogo";

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(200,45%,18%)] via-[hsl(200,45%,22%)] to-[hsl(195,40%,25%)]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
          <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
        </div>

        {/* Decorative Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
            backgroundSize: "150px 75px",
          }}
        />

        <div className="container relative py-10">
          <div className="flex flex-col items-center text-center">
            <div className="animate-fade-in-up relative">
              <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
              <img
                src={siteLogo}
                alt={SITE_LOGO_ALT}
                className="relative h-16 w-auto max-w-[min(320px,90vw)] cursor-pointer object-contain md:h-20"
                onClick={() => navigate("/")}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 404 Content */}
      <section className="to-secondary/20 relative overflow-hidden bg-gradient-to-b from-white py-20">
        {/* Background Decoration */}
        <div className="bg-primary/5 absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />

        <div className="container relative">
          <div className="mx-auto max-w-lg text-center">
            {/* Animated 404 */}
            <div className="relative mb-8">
              <div className="from-primary/20 to-primary/5 select-none bg-gradient-to-br bg-clip-text font-serif text-[clamp(4.5rem,22vw,12.5rem)] font-bold leading-none text-transparent md:text-[200px]">
                404
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-primary/10 animate-bounce-gentle flex h-24 w-24 items-center justify-center rounded-full">
                  <Search className="text-primary h-12 w-12" />
                </div>
              </div>
            </div>

            <h1 className="text-foreground mb-4 font-serif text-3xl font-bold md:text-4xl">
              Page Not Found
            </h1>
            <p className="text-muted-foreground mx-auto mb-10 max-w-md text-lg">
              The page you're looking for doesn't exist or has been moved to a
              new location.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                onClick={() => navigate("/")}
                size="lg"
                className="bg-primary hover:bg-primary/90 btn-shimmer text-white"
              >
                <Home className="mr-2 h-5 w-5" />
                Go Home
              </Button>
              <Button
                onClick={() => navigate("/browse")}
                size="lg"
                variant="outline"
                className="border-primary/30 hover:border-primary hover:bg-primary/5"
              >
                <BookOpen className="mr-2 h-5 w-5" />
                Browse Terms
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
